import React, { ReactNode, useEffect, useState } from 'react';
import { observer } from 'mobx-react';

import { ErrorPage } from '@components/common/errorPage';
import { webappClient } from '@lib/bugsnag';
import { onSharedSignOutElsewhere } from '@services/sharedSession';
import type { SessionStart } from '@stores/user';
import useStores from '@stores/useStores';
import { Loader } from '@ui-library/loader';
import { toast } from '@ui-library/toast/next';

/** How often a page checks the shared session. A sign-out on Lookout ends this session at the next check. */
const CHECK_INTERVAL_MS = 5 * 60 * 1000;
/** config-backend refreshes the ID token only in its last 5 minutes, so the renewal waits for that window. */
const RENEW_BEFORE_EXPIRY_MS = 4 * 60 * 1000;

/**
 * Starts the session before any route renders. It also keeps a session from the shared session of the web app
 * and Lookout (ACT2-879) current: at tab focus, every 5 minutes and shortly before the ID token expires. A
 * sign-out in another tab signs this tab out at once. When config-backend does not answer at start, the error
 * page asks for a reload, and the session stays. The error page also offers a log out.
 */
export const SessionGate = observer(({ children }: { children: ReactNode }) => {
  const { userStore } = useStores();
  const [start, setStart] = useState<'starting' | SessionStart>('starting');
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const started = start === 'ready';
  const { sessionMode, loggedIn, token } = userStore;

  useEffect(() => {
    userStore
      .startSession()
      .then((result) => setStart(result === 'unavailable' ? 'unavailable' : 'ready'))
      .catch((error: Error) => {
        webappClient.notify(error);
        setStart('ready');
      });
  }, []);

  useEffect(
    () =>
      onSharedSignOutElsewhere(() => {
        userStore.endSessionFromAnotherTab();
      }),
    [],
  );

  useEffect(() => {
    if (!started || sessionMode !== 'shared' || !loggedIn) {
      return undefined;
    }

    const check = () => {
      userStore.setAndRefreshToken(token);
    };
    const onFocus = () => {
      if (document.visibilityState === 'visible') {
        check();
      }
    };
    const expiresAt = userStore.getTokenExpirationTime();
    const renewal =
      expiresAt === null
        ? undefined
        : setTimeout(check, Math.max(expiresAt - RENEW_BEFORE_EXPIRY_MS - Date.now(), 0));
    const interval = setInterval(check, CHECK_INTERVAL_MS);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);

    return () => {
      clearTimeout(renewal);
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [started, sessionMode, loggedIn, token]);

  /**
   * The error page can come back on every load, for example while /getUser keeps failing. The session cookie is on
   * the API host, so clearing this site's data does not end the session. Only config-backend can end it.
   */
  const logOut = async () => {
    setIsLoggingOut(true);
    try {
      // Google and SSO users then leave for the Hosted UI sign-out page.
      await userStore.signOutFromSessionErrorPage();
      // The router shows the login page. A reload here could stop the move to the Hosted UI sign-out page.
      setStart('ready');
    } catch {
      // The session stays until config-backend ends it, so the user stays signed in, as in the log out dialog.
      toast.error('Could not log you out. Please try again.');
    }
    setIsLoggingOut(false);
  };

  if (start === 'unavailable') {
    return <ErrorPage onLogOut={logOut} isLoggingOut={isLoggingOut} />;
  }
  return started ? <>{children}</> : <Loader loading type="screen" />;
});
